import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Brain, Target, Clock, TrendingUp, Award, ArrowLeft, Crown, Lock, LayoutDashboard } from 'lucide-react';
import Header from '../components/Header.jsx';
import UpgradeModal from '../components/UpgradeModal.jsx';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboard } from '../contexts/DashboardContext.jsx';
import { colors, typography, radius, spacing, breakpoints } from '../../styles/theme.js';

const SpeedGamePage = () => {
  const navigate = useNavigate();
  const { tier } = useSubscription();
  const { isAuthenticated } = useAuth();
  const { saveSpeedSession, dashboardData, fetchDashboard } = useDashboard();
  const [mode, setMode] = useState('menu'); // menu, inspection, reaction
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [breakNeeded, setBreakNeeded] = useState(false);
  const [sessionStartStats, setSessionStartStats] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mobile = windowWidth < parseInt(breakpoints.mobile);

  // Fetch dashboard data on mount
  useEffect(() => {
    if (isAuthenticated && !dashboardData) {
      fetchDashboard();
    }
  }, [isAuthenticated, dashboardData, fetchDashboard]);
  
  // Inspection Time state
  const [inspectionDuration, setInspectionDuration] = useState(200);
  const [inspectionPhase, setInspectionPhase] = useState('ready');
  const [longerLine, setLongerLine] = useState('left');
  const [inspectionStats, setInspectionStats] = useState({ correct: 0, total: 0, recentAccuracy: [] });
  const [stimulusType, setStimulusType] = useState('lines');
  const [numChoices, setNumChoices] = useState(2);
  const [stimulusConfig, setStimulusConfig] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [inspectionTimers, setInspectionTimers] = useState([]);
  const [targetColor, setTargetColor] = useState('cyan');
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  
  // Reaction Time state
  const [reactionMode, setReactionMode] = useState(2);
  const [reactionPhase, setReactionPhase] = useState('waiting');
  const [currentStimulus, setCurrentStimulus] = useState(null);
  const [stimulusTime, setStimulusTime] = useState(null);
  const [reactionStats, setReactionStats] = useState({ times: [], average: 0, correct: 0, total: 0 });
  const [lastResponseCorrect, setLastResponseCorrect] = useState(null);
  
  const timerRef = useRef(null);
  const sessionTimerRef = useRef(null);

  // Fetch recommended starting values when entering a game mode
  useEffect(() => {
    const loadRecommendedSettings = async () => {
      if (!isAuthenticated) return;
      
      try {
        // Fetch fresh data if not already loaded
        if (!dashboardData) {
          await fetchDashboard();
          return; // Will retry on next render when dashboardData is available
        }

        const speedData = dashboardData?.speedTraining;
        if (!speedData) return;

        if (mode === 'inspection' && speedData.inspection) {
          const comboKey = `${stimulusType}-${numChoices}`;
          const comboStats = speedData.inspection.combos?.[comboKey];
          
          if (comboStats) {
            // Use last session duration if available, otherwise use avg duration
            const recommendedDuration = comboStats.lastSession?.duration || comboStats.avgDuration;
            
            // Adjust based on accuracy
            if (comboStats.avgAccuracy > 85 && recommendedDuration > 50) {
              // User is doing well, recommend slightly faster
              setInspectionDuration(Math.max(50, recommendedDuration - 10));
            } else if (comboStats.avgAccuracy < 70 && recommendedDuration < 500) {
              // User is struggling, recommend slightly slower
              setInspectionDuration(Math.min(500, recommendedDuration + 20));
            } else if (recommendedDuration) {
              // Use their last successful duration
              setInspectionDuration(recommendedDuration);
            }
            
            console.log(`Recommended inspection duration for ${comboKey}: ${inspectionDuration}ms (accuracy: ${comboStats.avgAccuracy}%)`);
          }
        }
      } catch (error) {
        console.error('Failed to load recommended settings:', error);
      }
    };

    if (mode !== 'menu') {
      loadRecommendedSettings();
    }
  }, [mode, stimulusType, numChoices, isAuthenticated, dashboardData, fetchDashboard]);

  // Save speed session when returning to menu
  const saveSession = async (gameType, stats) => {
    if (!isAuthenticated || !stats || stats.total === 0 && stats.times?.length === 0) {
      return; // Don't save if not authenticated or no trials completed
    }

    try {
      if (gameType === 'inspection') {
        const accuracy = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
        await saveSpeedSession({
          gameType: 'inspection',
          accuracy,
          trialsCompleted: stats.total,
          duration: inspectionDuration,
          stimulusType,
          numChoices
        });
        console.log('Inspection time session saved');
      } else if (gameType === 'reaction') {
        const trialsCompleted = stats.times?.length || 0;
        const accuracy = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
        if (trialsCompleted > 0) {
          await saveSpeedSession({
            gameType: 'reaction',
            accuracy,
            trialsCompleted,
            avgRT: stats.average,
            mode: reactionMode
          });
          console.log('Reaction time session saved with accuracy:', accuracy);
        }
      }
    } catch (error) {
      console.error('Failed to save speed session:', error);
    }
  };

  // Handle mode change and save session
  const handleReturnToMenu = async () => {
    // Save session before returning
    if (mode === 'inspection' && inspectionStats.total > 0) {
      await saveSession('inspection', inspectionStats);
    } else if (mode === 'reaction' && reactionStats.times.length > 0) {
      await saveSession('reaction', reactionStats);
    }

    // Reset state
    setMode('menu');
    setSessionTime(0);
    clearInspectionTimers();
    setIsPaused(false);
    clearTimeout(timerRef.current);
  };

  // Session timer
  useEffect(() => {
    if (mode !== 'menu') {
      sessionTimerRef.current = setInterval(() => {
        setSessionTime(prev => {
          const newTime = prev + 1;
          if (newTime > 0 && newTime % 210 === 0) {
            setBreakNeeded(true);
          }
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(sessionTimerRef.current);
  }, [mode]);

  // Inspection Time Task
  const startInspectionTrial = () => {
    setInspectionPhase('ready');
    
    const generateStimuli = () => {
      const configs = [];
      const correctIndex = Math.floor(Math.random() * numChoices);
      
      if (stimulusType === 'lines') {
        const sizes = [170, 175, 180, 185, 190, 195];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'line',
            height: i === correctIndex ? 200 : sizes[Math.floor(Math.random() * sizes.length)],
            color: colors.primary
          });
        }
      } else if (stimulusType === 'circles') {
        const sizes = [52, 55, 58, 61, 64];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'circle',
            size: i === correctIndex ? 67 : sizes[Math.floor(Math.random() * sizes.length)],
            color: colors.primary
          });
        }
      } else if (stimulusType === 'squares') {
        const sizes = [62, 65, 68, 71, 74];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'square',
            size: i === correctIndex ? 77 : sizes[Math.floor(Math.random() * sizes.length)],
            color: colors.primary
          });
        }
      } else if (stimulusType === 'colors') {
        const colorOptions = [
          { name: 'RED', hex: '#ef4444' },
          { name: 'BLUE', hex: '#3b82f6' },
          { name: 'GREEN', hex: '#10b981' },
          { name: 'YELLOW', hex: '#eab308' },
          { name: 'CYAN', hex: '#06b6d4' },
          { name: 'MAGENTA', hex: '#d946ef' },
          { name: 'ORANGE', hex: '#f97316' },
          { name: 'PURPLE', hex: '#a855f7' },
          { name: 'PINK', hex: '#ec4899' },
          { name: 'LIME', hex: '#84cc16' }
        ];
        
        const targetColorObj = colorOptions[Math.floor(Math.random() * colorOptions.length)];
        setTargetColor(targetColorObj.name);
        
        const distractorColors = colorOptions.filter(c => c.hex !== targetColorObj.hex);
        
        for (let i = 0; i < numChoices; i++) {
          if (i === correctIndex) {
            configs.push({
              type: 'square',
              size: 60,
              color: targetColorObj.hex
            });
          } else {
            const distractor = distractorColors[Math.floor(Math.random() * distractorColors.length)];
            configs.push({
              type: 'square',
              size: 60,
              color: distractor.hex
            });
          }
        }
      } else if (stimulusType === 'angles') {
        const angles = [60, 65, 70, 75, 80, 85];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'angle',
            rotation: i === correctIndex ? 90 : angles[Math.floor(Math.random() * angles.length)],
            color: colors.primary
          });
        }
      } else if (stimulusType === 'brightness') {
        const brightnesses = ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'square',
            size: 60,
            color: i === correctIndex ? '#1e40af' : brightnesses[Math.floor(Math.random() * brightnesses.length)]
          });
        }
      } else if (stimulusType === 'thickness') {
        const widths = [3, 4, 5, 6, 7];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'line',
            height: 180,
            width: i === correctIndex ? 8 : widths[Math.floor(Math.random() * widths.length)],
            color: colors.primary
          });
        }
      } else if (stimulusType === 'position') {
        const shifts = [5, 10, 15, 20, 25];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'square',
            size: 60,
            color: colors.primary,
            yShift: i === correctIndex ? -15 : shifts[Math.floor(Math.random() * shifts.length)]
          });
        }
      } else if (stimulusType === 'patterns') {
        const dotCounts = [2, 3, 4, 5];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'dots',
            count: i === correctIndex ? 6 : dotCounts[Math.floor(Math.random() * dotCounts.length)],
            color: colors.primary
          });
        }
      } else if (stimulusType === 'complex') {
        const sizes = [170, 180, 190];
        const colorOptions = [colors.primary, colors.secondary, colors.accent];
        for (let i = 0; i < numChoices; i++) {
          configs.push({
            type: 'line',
            height: i === correctIndex ? 200 : sizes[Math.floor(Math.random() * sizes.length)],
            color: i === correctIndex ? colors.primary : colorOptions[Math.floor(Math.random() * colorOptions.length)]
          });
        }
      }
      
      return { configs, correctIndex };
    };
    
    const { configs, correctIndex } = generateStimuli();
    setStimulusConfig(configs);
    setLongerLine(correctIndex);
    
    const timer1 = setTimeout(() => {
      setInspectionPhase('showing');
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const timer2 = setTimeout(() => {
            setInspectionPhase('masked');
          }, inspectionDuration);
          setInspectionTimers(prev => [...prev, timer2]);
        });
      });
    }, 1000 + Math.random() * 1000);
    
    setInspectionTimers(prev => [...prev, timer1]);
  };

  const clearInspectionTimers = () => {
    inspectionTimers.forEach(timer => clearTimeout(timer));
    setInspectionTimers([]);
  };

  const skipTrial = () => {
    clearInspectionTimers();
    setInspectionPhase('ready');
    setTimeout(() => startInspectionTrial(), 100);
  };

  const handleInspectionResponse = (choiceIndex) => {
    if (inspectionPhase !== 'masked' || isPaused) return;
    
    const correct = choiceIndex === longerLine;
    const newStats = {
      ...inspectionStats,
      correct: inspectionStats.correct + (correct ? 1 : 0),
      total: inspectionStats.total + 1,
      recentAccuracy: [...inspectionStats.recentAccuracy, correct ? 1 : 0].slice(-20)
    };
    setInspectionStats(newStats);
    
    setInspectionPhase('feedback');
    const feedbackTimer = setTimeout(() => {
      startInspectionTrial();
    }, 800);
    setInspectionTimers(prev => [...prev, feedbackTimer]);
    
    if (newStats.recentAccuracy.length >= 20) {
      const recentAcc = newStats.recentAccuracy.reduce((a, b) => a + b, 0) / 20;
      if (recentAcc > 0.85 && inspectionDuration > 1) {
        setInspectionDuration(prev => Math.max(1, prev - 10));
      } else if (recentAcc < 0.65) {
        setInspectionDuration(prev => Math.min(300, prev + 10));
      }
    }
  };

  // Reaction Time Task
  const startReactionTrial = () => {
    setReactionPhase('waiting');
    const delay = 1000 + Math.random() * 2000;
    
    timerRef.current = setTimeout(() => {
      const stimuli = reactionMode === 2 ? ['left', 'right'] :
                     reactionMode === 4 ? ['up', 'down', 'left', 'right'] :
                     ['up', 'down', 'left', 'right', 'up-left', 'up-right', 'down-left', 'down-right'];
      
      const stimulus = stimuli[Math.floor(Math.random() * stimuli.length)];
      setCurrentStimulus(stimulus);
      setStimulusTime(Date.now());
      setReactionPhase('stimulus');
    }, delay);
  };

  const handleReactionResponse = (response) => {
    if (reactionPhase !== 'stimulus') return;
    
    const reactionTime = Date.now() - stimulusTime;
    const correct = response === currentStimulus;
    
    setLastResponseCorrect(correct);
    
    if (correct) {
      const newTimes = [...reactionStats.times, reactionTime].slice(-50);
      const average = newTimes.reduce((a, b) => a + b, 0) / newTimes.length;
      setReactionStats({ 
        times: newTimes, 
        average,
        correct: reactionStats.correct + 1,
        total: reactionStats.total + 1
      });
    } else {
      setReactionStats({
        ...reactionStats,
        total: reactionStats.total + 1
      });
    }
    
    setReactionPhase('responded');
    setTimeout(() => {
      setLastResponseCorrect(null);
      startReactionTrial();
    }, correct ? 600 : 1000); // Show error feedback longer
  };

  useEffect(() => {
    if (mode === 'inspection') {
      startInspectionTrial();
    }
    return () => {
      clearInspectionTimers();
    };
  }, [mode]);

  useEffect(() => {
    if (mode === 'reaction' && reactionPhase === 'waiting') {
      startReactionTrial();
    }
    return () => clearTimeout(timerRef.current);
  }, [mode, reactionMode]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (mode === 'reaction' && reactionPhase === 'stimulus') {
        const keyMap = {
          'ArrowLeft': 'left',
          'ArrowRight': 'right',
          'ArrowUp': 'up',
          'ArrowDown': 'down',
          'q': 'up-left',
          'e': 'up-right',
          'z': 'down-left',
          'c': 'down-right'
        };
        if (keyMap[e.key]) {
          handleReactionResponse(keyMap[e.key]);
        }
      } else if (mode === 'inspection') {
        if (e.key === 'Escape') {
          if (!isPaused) {
            skipTrial();
          }
        } else if (inspectionPhase === 'masked' && !isPaused) {
          const keyMap = ['1', '2', '3', '4', '5', '6'];
          const index = keyMap.indexOf(e.key);
          if (index !== -1 && index < numChoices) {
            handleInspectionResponse(index);
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [mode, reactionPhase, inspectionPhase, numChoices, isPaused]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAccuracy = () => {
    if (inspectionStats.total === 0) return 0;
    return ((inspectionStats.correct / inspectionStats.total) * 100).toFixed(1);
  };

  const getStimulusIcon = (stimulus) => {
    const arrows = {
      'left': '←', 'right': '→', 'up': '↑', 'down': '↓',
      'up-left': '↖', 'up-right': '↗', 'down-left': '↙', 'down-right': '↘'
    };
    return arrows[stimulus] || '→';
  };

  // Check if user has Pro access
  const hasProAccess = tier === 'pro' || tier === 'paid';
  
  const handleModeSelect = (selectedMode) => {
    if (!hasProAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setMode(selectedMode);
  };

  // Shared styles
  const cardStyle = {
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    padding: mobile ? spacing[4] : spacing[6],
    transition: 'all 0.2s ease'
  };

  const buttonPrimaryStyle = {
    padding: '0.75rem 1.5rem',
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    color: colors.textSecondary,
    fontSize: typography.sm,
    fontWeight: typography.semibold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: typography.fontSans
  };

  return (
    <>
      {mode === 'menu' && <Header />}
      <div style={{
        minHeight: mode === 'menu' ? 'calc(100vh - 64px)' : '100vh',
        background: colors.bgPrimary,
        fontFamily: typography.fontSans,
        color: colors.textPrimary
      }}>
        {/* Break Modal */}
        {breakNeeded && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50
          }}>
            <div style={{
              ...cardStyle,
              maxWidth: '400px',
              textAlign: 'center'
            }}>
              <Clock size={48} color={colors.primary} style={{ margin: '0 auto 1rem' }} />
              <h2 style={{
                fontSize: typography['2xl'],
                fontWeight: typography.bold,
                color: colors.primary,
                marginBottom: '0.5rem'
              }}>
                Rest Period
              </h2>
              <p style={{
                color: colors.textTertiary,
                marginBottom: '1.5rem'
              }}>
                Take a 30-second break to maintain peak performance
              </p>
              <button
                onClick={() => setBreakNeeded(false)}
                style={{
                  ...buttonPrimaryStyle,
                  width: '100%',
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  border: 'none',
                  color: 'white',
                  fontSize: typography.base
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Continue Training
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {mode === 'menu' && (
          <div style={{
            maxWidth: '1000px',
            margin: '0 auto',
            padding: mobile ? '2rem 1rem' : '3rem 2rem'
          }}>
            {/* Back to Dashboard Button - At the top (only for authenticated users) */}
            {isAuthenticated && (
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  width: '100%',
                  background: 'rgba(100, 116, 139, 0.15)',
                  border: '1px solid rgba(100, 116, 139, 0.3)',
                  borderRadius: radius.md,
                  padding: mobile ? spacing[2] : spacing[3],
                  fontSize: mobile ? typography.xs : typography.sm,
                  fontWeight: typography.semibold,
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: spacing[2],
                  transition: 'all 0.2s ease',
                  marginBottom: spacing[4],
                  fontFamily: typography.fontSans
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(100, 116, 139, 0.25)';
                  e.target.style.borderColor = 'rgba(100, 116, 139, 0.5)';
                  e.target.style.color = colors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(100, 116, 139, 0.15)';
                  e.target.style.borderColor = 'rgba(100, 116, 139, 0.3)';
                  e.target.style.color = colors.textSecondary;
                }}
              >
                <LayoutDashboard size={mobile ? 14 : 16} />
                Back to Dashboard
              </button>
            )}

            <div style={{ textAlign: 'center', marginBottom: mobile ? '2rem' : '3rem' }}>
              <h1 style={{
                fontSize: mobile ? typography['2xl'] : typography['4xl'],
                fontWeight: typography.extrabold,
                color: colors.primary,
                marginBottom: '0.75rem'
              }}>
                Speed Training
              </h1>
              <p style={{
                color: colors.textTertiary,
                fontSize: mobile ? typography.sm : typography.lg
              }}>
                Optimize neural processing speed through targeted cognitive exercises
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: mobile ? '1rem' : '1.5rem',
              marginBottom: '2rem'
            }}>
              {/* Inspection Time Card */}
              <div
                onClick={() => handleModeSelect('inspection')}
                style={{
                  ...cardStyle,
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = colors.borderFocus;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                <Target size={40} color={colors.primary} style={{ marginBottom: '1rem' }} />
                <h3 style={{
                  fontSize: typography['2xl'],
                  fontWeight: typography.bold,
                  color: colors.primary,
                  marginBottom: '0.5rem'
                }}>
                  Inspection Time
                </h3>
                <p style={{
                  color: colors.textTertiary,
                  fontSize: typography.sm,
                  marginBottom: '1rem',
                  lineHeight: typography.relaxed
                }}>
                  Train rapid visual discrimination. Research shows 0.5 correlation with IQ (Burns & Nettelbeck, 2003).
                  Progressive difficulty from 200ms to extreme sub-millisecond thresholds.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: typography.xs }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.textMuted }}>
                    <span>Best Duration:</span>
                    <span style={{ color: colors.primary, fontWeight: typography.semibold }}>
                      {dashboardData?.speedTraining?.inspection?.globalStats?.bestDuration 
                        ? `${dashboardData.speedTraining.inspection.globalStats.bestDuration}ms` 
                        : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.textMuted }}>
                    <span>Avg Accuracy:</span>
                    <span style={{ color: colors.primary, fontWeight: typography.semibold }}>
                      {dashboardData?.speedTraining?.inspection?.globalStats?.avgAccuracy 
                        ? `${Math.round(dashboardData.speedTraining.inspection.globalStats.avgAccuracy)}%` 
                        : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.textMuted }}>
                    <span>Total Trials:</span>
                    <span style={{ color: colors.primary, fontWeight: typography.semibold }}>
                      {dashboardData?.speedTraining?.inspection?.globalStats?.totalTrials || 0}
                    </span>
                  </div>
                </div>
                
                {/* Pro Lock Overlay */}
                {!hasProAccess && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(4px)',
                    borderRadius: radius.lg,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    <Lock size={48} color={colors.warning} style={{ marginBottom: '1rem' }} />
                    <div style={{
                      color: 'white',
                      fontSize: typography.lg,
                      fontWeight: typography.semibold,
                      textAlign: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      Pro Feature
                    </div>
                    <div style={{
                      padding: '0.5rem 1rem',
                      background: colors.warning,
                      color: '#1f2937',
                      borderRadius: radius.md,
                      fontSize: typography.sm,
                      fontWeight: typography.semibold,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Crown size={16} />
                      Upgrade to Unlock
                    </div>
                  </div>
                )}
              </div>

              {/* Reaction Time Card */}
              <div
                onClick={() => handleModeSelect('reaction')}
                style={{
                  ...cardStyle,
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = colors.borderFocus;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                <Zap size={40} color={colors.primary} style={{ marginBottom: '1rem' }} />
                <h3 style={{
                  fontSize: typography['2xl'],
                  fontWeight: typography.bold,
                  color: colors.primary,
                  marginBottom: '0.5rem'
                }}>
                  Choice Reaction
                </h3>
                <p style={{
                  color: colors.textTertiary,
                  fontSize: typography.sm,
                  marginBottom: '1rem',
                  lineHeight: typography.relaxed
                }}>
                  Train rapid stimulus-response pathways. Progress from 2-choice (baseline 200ms) to 8-choice complexity.
                  Master sub-400ms responses across increasing discrimination challenges.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: typography.xs }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.textMuted }}>
                    <span>Current Mode:</span>
                    <span style={{ color: colors.primary, fontWeight: typography.semibold }}>{reactionMode}-Choice</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.textMuted }}>
                    <span>Average RT:</span>
                    <span style={{ color: colors.primary, fontWeight: typography.semibold }}>
                      {dashboardData?.speedTraining?.reaction?.globalStats?.avgRT 
                        ? `${Math.round(dashboardData.speedTraining.reaction.globalStats.avgRT)}ms` 
                        : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.textMuted }}>
                    <span>Total Trials:</span>
                    <span style={{ color: colors.primary, fontWeight: typography.semibold }}>
                      {dashboardData?.speedTraining?.reaction?.globalStats?.totalTrials || 0}
                    </span>
                  </div>
                </div>
                
                {/* Pro Lock Overlay */}
                {!hasProAccess && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(4px)',
                    borderRadius: radius.lg,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    <Lock size={48} color={colors.warning} style={{ marginBottom: '1rem' }} />
                    <div style={{
                      color: 'white',
                      fontSize: typography.lg,
                      fontWeight: typography.semibold,
                      textAlign: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      Pro Feature
                    </div>
                    <div style={{
                      padding: '0.5rem 1rem',
                      background: colors.warning,
                      color: '#1f2937',
                      borderRadius: radius.md,
                      fontSize: typography.sm,
                      fontWeight: typography.semibold,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Crown size={16} />
                      Upgrade to Unlock
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info Panel */}
            <div style={{
              ...cardStyle,
              background: 'rgba(96, 165, 250, 0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <Brain size={24} color={colors.primary} />
                <h3 style={{
                  fontSize: typography.lg,
                  fontWeight: typography.bold,
                  color: colors.primary
                }}>
                  Training Protocol
                </h3>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                fontSize: typography.sm
              }}>
                <div>
                  <p style={{ color: colors.textTertiary, marginBottom: '0.25rem' }}>Session Duration</p>
                  <p style={{ color: colors.primary, fontWeight: typography.semibold }}>15-20 minutes</p>
                </div>
                <div>
                  <p style={{ color: colors.textTertiary, marginBottom: '0.25rem' }}>Frequency</p>
                  <p style={{ color: colors.primary, fontWeight: typography.semibold }}>5-6x per week</p>
                </div>
                <div>
                  <p style={{ color: colors.textTertiary, marginBottom: '0.25rem' }}>Rest Intervals</p>
                  <p style={{ color: colors.primary, fontWeight: typography.semibold }}>30s every 3-4 min</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {mode === 'inspection' && (
          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto', 
            padding: mobile ? '0.75rem' : '2rem' 
          }}>
            {/* Back button and session time */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: mobile ? '0.75rem' : '1.5rem' }}>
              <button
                onClick={handleReturnToMenu}
                style={{
                  ...buttonPrimaryStyle,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: mobile ? '0.5rem 1rem' : '0.75rem 1.5rem',
                  fontSize: mobile ? typography.xs : typography.sm
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
                onMouseLeave={(e) => e.currentTarget.style.background = colors.bgCard}
              >
                <ArrowLeft size={mobile ? 14 : 18} />
                Back to Menu
              </button>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: mobile ? '0.65rem' : typography.xs, color: colors.textMuted, marginBottom: mobile ? '0.15rem' : '0.25rem' }}>Session Time</p>
                <p style={{ fontSize: mobile ? typography.base : typography.lg, fontWeight: typography.bold, color: colors.primary }}>{formatTime(sessionTime)}</p>
              </div>
            </div>

            {/* Stats Bar */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: mobile ? '0.5rem' : '1rem',
              marginBottom: mobile ? '0.75rem' : '1rem'
            }}>
              {[
                { label: 'DURATION', value: `${inspectionDuration}ms` },
                { label: 'ACCURACY', value: `${getAccuracy()}%` },
                { label: 'CORRECT', value: inspectionStats.correct },
                { label: 'TRIALS', value: inspectionStats.total }
              ].map((stat, idx) => (
                <div key={idx} style={{ ...cardStyle, textAlign: 'center', padding: mobile ? '0.5rem' : '1rem' }}>
                  <p style={{ fontSize: mobile ? '0.6rem' : typography.xs, color: colors.textMuted, marginBottom: mobile ? '0.15rem' : '0.25rem' }}>{stat.label}</p>
                  <p style={{ fontSize: mobile ? typography.base : typography['2xl'], fontWeight: typography.bold, color: colors.primary }}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Pause/Skip Controls */}
            <div style={{ display: 'flex', gap: mobile ? '0.5rem' : '0.75rem', marginBottom: mobile ? '0.75rem' : '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  if (isPaused) {
                    setIsPaused(false);
                    clearInspectionTimers();
                    setInspectionPhase('ready');
                    setTimeout(() => startInspectionTrial(), 100);
                  } else {
                    setIsPaused(true);
                    clearInspectionTimers();
                    setInspectionPhase('ready');
                  }
                }}
                style={{
                  ...buttonPrimaryStyle,
                  padding: mobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
                  fontSize: mobile ? typography.xs : typography.sm,
                  background: isPaused ? colors.successText : '#f59e0b',
                  color: isPaused ? 'white' : '#1f2937',
                  border: 'none',
                  fontWeight: typography.bold
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                {isPaused ? '▶ Resume' : '⏸ Pause'}
              </button>
              <button
                onClick={skipTrial}
                disabled={isPaused}
                style={{
                  ...buttonPrimaryStyle,
                  padding: mobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
                  fontSize: mobile ? typography.xs : typography.sm,
                  opacity: isPaused ? 0.3 : 1,
                  cursor: isPaused ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !isPaused && (e.currentTarget.style.background = colors.bgHover)}
                onMouseLeave={(e) => !isPaused && (e.currentTarget.style.background = colors.bgCard)}
              >
                ⏭ Skip (ESC)
              </button>
              {mobile && (
                <button
                  onClick={() => setShowMobileSettings(!showMobileSettings)}
                  style={{
                    ...buttonPrimaryStyle,
                    padding: '0.6rem 1rem',
                    fontSize: typography.xs,
                    background: showMobileSettings ? colors.primary : colors.bgCard,
                    color: showMobileSettings ? 'white' : colors.textSecondary,
                    borderColor: showMobileSettings ? colors.primary : colors.border
                  }}
                  onMouseEnter={(e) => !showMobileSettings && (e.currentTarget.style.background = colors.bgHover)}
                  onMouseLeave={(e) => !showMobileSettings && (e.currentTarget.style.background = colors.bgCard)}
                >
                  ⚙ Settings {showMobileSettings ? '▲' : '▼'}
                </button>
              )}
            </div>

            {/* Duration Controls - Collapsible on mobile */}
            {(!mobile || showMobileSettings) && (
              <div style={{ 
                ...cardStyle, 
                marginBottom: '1rem'
              }}>
                <p style={{ 
                  color: colors.textTertiary, 
                  fontSize: mobile ? typography.xs : typography.sm, 
                  fontWeight: typography.semibold, 
                  marginBottom: '0.75rem' 
                }}>
                  Manual Adjustment:
                </p>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: mobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
                  gap: mobile ? '0.5rem' : '0.75rem'
                }}>
                  {[
                    { label: '+10ms', value: 10 },
                    { label: '+5ms', value: 5 },
                    { label: '+1ms', value: 1 },
                    { label: '-1ms', value: -1 },
                    { label: '-5ms', value: -5 },
                    { label: '-10ms', value: -10 }
                  ].map(btn => (
                    <button
                      key={btn.label}
                      onClick={() => {
                        setInspectionDuration(prev => btn.value > 0 ? Math.min(300, prev + btn.value) : Math.max(1, prev + btn.value));
                        if (!isPaused) {
                          clearInspectionTimers();
                          setInspectionPhase('ready');
                          setTimeout(() => startInspectionTrial(), 100);
                        }
                      }}
                      style={{
                        ...buttonPrimaryStyle,
                        padding: mobile ? '0.6rem 0.5rem' : '0.5rem 0.75rem',
                        fontSize: mobile ? '0.7rem' : typography.xs
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
                      onMouseLeave={(e) => e.currentTarget.style.background = colors.bgCard}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setInspectionDuration(200);
                    if (!isPaused) {
                      clearInspectionTimers();
                      setInspectionPhase('ready');
                      setTimeout(() => startInspectionTrial(), 100);
                    }
                  }}
                  style={{
                    ...buttonPrimaryStyle,
                    padding: mobile ? '0.6rem' : '0.5rem 0.75rem',
                    fontSize: typography.xs,
                    width: '100%',
                    marginTop: '0.5rem'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
                  onMouseLeave={(e) => e.currentTarget.style.background = colors.bgCard}
                >
                  Reset (200ms)
                </button>
              </div>
            )}

            {/* Stimulus Type and Choices Selector - Collapsible on mobile */}
            {(!mobile || showMobileSettings) && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: mobile ? '1fr' : '2fr 1fr', 
                gap: '1rem', 
                marginBottom: '1rem' 
              }}>
                <div style={cardStyle}>
                  <p style={{ color: colors.textTertiary, fontSize: typography.xs, marginBottom: '0.5rem', fontWeight: typography.semibold }}>STIMULUS TYPE:</p>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: mobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)', 
                    gap: '0.5rem' 
                  }}>
                    {[
                      { value: 'lines', label: 'Lines' },
                      { value: 'circles', label: 'Circles' },
                      { value: 'squares', label: 'Squares' },
                      { value: 'colors', label: 'Colors' },
                      { value: 'angles', label: 'Angles' },
                      { value: 'brightness', label: 'Bright' },
                      { value: 'thickness', label: 'Thick' },
                      { value: 'position', label: 'Position' },
                      { value: 'patterns', label: 'Dots' },
                      { value: 'complex', label: 'Complex' }
                    ].map(type => (
                      <button
                        key={type.value}
                        onClick={() => {
                          clearInspectionTimers();
                          setStimulusType(type.value);
                          setInspectionStats({ correct: 0, total: 0, recentAccuracy: [] });
                          setInspectionPhase('ready');
                          if (!isPaused) {
                            setTimeout(() => startInspectionTrial(), 100);
                          }
                        }}
                        style={{
                          ...buttonPrimaryStyle,
                          padding: mobile ? '0.6rem 0.3rem' : '0.5rem',
                          fontSize: mobile ? '0.65rem' : typography.xs,
                          background: stimulusType === type.value ? colors.primary : colors.bgCard,
                          color: stimulusType === type.value ? 'white' : colors.textSecondary,
                          borderColor: stimulusType === type.value ? colors.primary : colors.border
                        }}
                        onMouseEnter={(e) => stimulusType !== type.value && (e.currentTarget.style.background = colors.bgHover)}
                        onMouseLeave={(e) => stimulusType !== type.value && (e.currentTarget.style.background = colors.bgCard)}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={cardStyle}>
                  <p style={{ color: colors.textTertiary, fontSize: typography.xs, marginBottom: '0.5rem', fontWeight: typography.semibold }}>CHOICES:</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                    {[2, 3, 4, 5, 6].map(num => (
                      <button
                        key={num}
                        onClick={() => {
                          clearInspectionTimers();
                          setNumChoices(num);
                          setInspectionStats({ correct: 0, total: 0, recentAccuracy: [] });
                          setInspectionPhase('ready');
                          if (!isPaused) {
                            setTimeout(() => startInspectionTrial(), 100);
                          }
                        }}
                        style={{
                          ...buttonPrimaryStyle,
                          padding: mobile ? '0.6rem 0.3rem' : '0.5rem',
                          fontSize: mobile ? '0.7rem' : typography.xs,
                          background: numChoices === num ? colors.primary : colors.bgCard,
                          color: numChoices === num ? 'white' : colors.textSecondary,
                          borderColor: numChoices === num ? colors.primary : colors.border
                        }}
                        onMouseEnter={(e) => numChoices !== num && (e.currentTarget.style.background = colors.bgHover)}
                        onMouseLeave={(e) => numChoices !== num && (e.currentTarget.style.background = colors.bgCard)}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Main Display */}
            <div style={{
              ...cardStyle,
              height: mobile ? '220px' : '400px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: mobile ? '0.75rem' : '1.5rem',
              background: 'rgba(96, 165, 250, 0.03)'
            }}>
              {isPaused ? (
                <div style={{ textAlign: 'center', padding: mobile ? '0 0.5rem' : '0' }}>
                  <div style={{ fontSize: mobile ? '3rem' : '4rem', marginBottom: mobile ? '0.5rem' : '1rem' }}>⏸</div>
                  <p style={{ color: colors.primary, fontSize: mobile ? typography.xl : typography['2xl'], fontWeight: typography.bold, marginBottom: mobile ? '0.25rem' : '0.5rem' }}>Paused</p>
                  <p style={{ color: colors.textTertiary, fontSize: mobile ? typography.xs : typography.sm }}>Make adjustments or press Resume to continue</p>
                </div>
              ) : (
                <>
                  {inspectionPhase === 'ready' && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: mobile ? '48px' : '64px',
                        height: mobile ? '48px' : '64px',
                        border: `${mobile ? '3px' : '4px'} solid ${colors.primary}`,
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: mobile ? '0 auto 0.75rem' : '0 auto 1rem'
                      }} />
                      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                      <p style={{ color: colors.textTertiary, fontSize: mobile ? typography.base : typography.lg }}>Preparing stimulus...</p>
                    </div>
                  )}

                  {inspectionPhase === 'showing' && (
                    <div style={{ display: 'flex', gap: mobile ? '1rem' : '2rem', alignItems: 'flex-end', justifyContent: 'center' }}>
                      {stimulusConfig.map((config, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          transform: config.yShift ? `translateY(${config.yShift}px)` : 'none'
                        }}>
                          {config.type === 'line' && (
                            <div style={{
                              width: `${config.width || 4}px`,
                              height: `${config.height}px`,
                              backgroundColor: config.color
                            }} />
                          )}
                          {config.type === 'circle' && (
                            <div style={{
                              width: `${config.size}px`,
                              height: `${config.size}px`,
                              backgroundColor: config.color,
                              borderRadius: '50%'
                            }} />
                          )}
                          {config.type === 'square' && (
                            <div style={{
                              width: `${config.size}px`,
                              height: `${config.size}px`,
                              backgroundColor: config.color
                            }} />
                          )}
                          {config.type === 'angle' && (
                            <div style={{
                              width: '64px',
                              height: '4px',
                              backgroundColor: config.color,
                              transform: `rotate(${config.rotation}deg)`,
                              transformOrigin: 'left center'
                            }} />
                          )}
                          {config.type === 'dots' && (
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '4px',
                              width: '80px',
                              height: '80px',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {Array(config.count).fill(0).map((_, dotIdx) => (
                                <div key={dotIdx} style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: config.color
                                }} />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {inspectionPhase === 'masked' && (
                    <div style={{ textAlign: 'center', padding: mobile ? '0 0.5rem' : '0' }}>
                      <p style={{ color: colors.primary, fontSize: mobile ? typography.base : typography.lg, fontWeight: typography.semibold }}>
                        {stimulusType === 'lines' && 'Which line was longest?'}
                        {stimulusType === 'circles' && 'Which circle was largest?'}
                        {stimulusType === 'squares' && 'Which square was largest?'}
                        {stimulusType === 'colors' && `Which was ${targetColor}?`}
                        {stimulusType === 'angles' && 'Which line was most vertical?'}
                        {stimulusType === 'brightness' && 'Which was BRIGHTEST?'}
                        {stimulusType === 'thickness' && 'Which line was THICKEST?'}
                        {stimulusType === 'position' && 'Which was HIGHEST?'}
                        {stimulusType === 'patterns' && 'Which had MOST dots?'}
                        {stimulusType === 'complex' && 'Which was longest AND blue?'}
                      </p>
                    </div>
                  )}

                  {inspectionPhase === 'feedback' && (
                    <div style={{ textAlign: 'center' }}>
                      <Award size={mobile ? 48 : 64} color={colors.primary} style={{ margin: mobile ? '0 auto 0.75rem' : '0 auto 1rem', animation: 'pulse 1s ease-in-out infinite' }} />
                      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
                      <p style={{ fontSize: mobile ? typography.xl : typography['2xl'], fontWeight: typography.bold, color: colors.primary }}>
                        {inspectionStats.correct}/{inspectionStats.total}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Response Buttons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${numChoices}, 1fr)`,
              gap: mobile ? '0.5rem' : '1rem',
              marginBottom: mobile ? '0.5rem' : '1rem'
            }}>
              {Array(numChoices).fill(0).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => !isPaused && inspectionPhase === 'masked' && handleInspectionResponse(idx)}
                  disabled={isPaused || inspectionPhase !== 'masked'}
                  style={{
                    ...cardStyle,
                    padding: mobile ? '1rem' : '1.5rem',
                    fontSize: mobile ? typography.xl : typography['2xl'],
                    fontWeight: typography.bold,
                    color: !isPaused && inspectionPhase === 'masked' ? colors.primary : colors.textMuted,
                    background: !isPaused && inspectionPhase === 'masked' ? colors.bgCard : 'rgba(15, 23, 42, 0.6)',
                    cursor: !isPaused && inspectionPhase === 'masked' ? 'pointer' : 'not-allowed',
                    opacity: !isPaused && inspectionPhase === 'masked' ? 1 : 0.5
                  }}
                  onMouseEnter={(e) => {
                    if (!isPaused && inspectionPhase === 'masked') {
                      e.currentTarget.style.background = colors.bgHover;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isPaused && inspectionPhase === 'masked') {
                      e.currentTarget.style.background = colors.bgCard;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            {!mobile && (
              <p style={{ textAlign: 'center', color: colors.textTertiary, fontSize: typography.sm }}>
                Use number keys (1-{numChoices}) to respond quickly
              </p>
            )}
          </div>
        )}

        {mode === 'reaction' && (
          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto', 
            padding: mobile ? '1rem' : '2rem' 
          }}>
            {/* Back button and session time */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <button
                onClick={handleReturnToMenu}
                style={{
                  ...buttonPrimaryStyle,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
                onMouseLeave={(e) => e.currentTarget.style.background = colors.bgCard}
              >
                <ArrowLeft size={18} />
                Back to Menu
              </button>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: typography.xs, color: colors.textMuted, marginBottom: '0.25rem' }}>Session Time</p>
                <p style={{ fontSize: typography.lg, fontWeight: typography.bold, color: colors.primary }}>{formatTime(sessionTime)}</p>
              </div>
            </div>

            {/* Stats Bar */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: mobile ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
              gap: mobile ? '0.5rem' : '1rem',
              marginBottom: mobile ? '1rem' : '2rem'
            }}>
              {[
                { label: 'MODE', value: `${reactionMode}-Choice` },
                { label: 'AVG RT', value: reactionStats.average ? `${Math.round(reactionStats.average)}ms` : '—' },
                { label: 'ACCURACY', value: reactionStats.total > 0 ? `${Math.round((reactionStats.correct / reactionStats.total) * 100)}%` : '—' }
              ].map((stat, idx) => (
                <div key={idx} style={{ ...cardStyle, textAlign: 'center', padding: mobile ? '0.75rem' : '1rem' }}>
                  <p style={{ fontSize: typography.xs, color: colors.textMuted, marginBottom: '0.25rem' }}>{stat.label}</p>
                  <p style={{ fontSize: mobile ? typography.lg : typography['2xl'], fontWeight: typography.bold, color: colors.primary }}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Mode Selector */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: mobile ? '1rem' : '2rem', justifyContent: 'center' }}>
              {[2, 4, 8].map(modeNum => (
                <button
                  key={modeNum}
                  onClick={() => {
                    setReactionMode(modeNum);
                    setReactionPhase('waiting');
                    setReactionStats({ times: [], average: 0, correct: 0, total: 0 });
                    setLastResponseCorrect(null);
                  }}
                  style={{
                    ...buttonPrimaryStyle,
                    background: reactionMode === modeNum ? colors.primary : colors.bgCard,
                    color: reactionMode === modeNum ? 'white' : colors.textSecondary,
                    borderColor: reactionMode === modeNum ? colors.primary : colors.border
                  }}
                  onMouseEnter={(e) => reactionMode !== modeNum && (e.currentTarget.style.background = colors.bgHover)}
                  onMouseLeave={(e) => reactionMode !== modeNum && (e.currentTarget.style.background = colors.bgCard)}
                >
                  {modeNum}-Choice
                </button>
              ))}
            </div>

            {/* Main Display */}
            <div style={{
              ...cardStyle,
              height: mobile ? '250px' : '400px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: mobile ? '1rem' : '1.5rem',
              background: 'rgba(96, 165, 250, 0.03)'
            }}>
              {reactionPhase === 'waiting' && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: colors.primary,
                    borderRadius: '50%',
                    margin: '0 auto 1rem',
                    animation: 'pulse 1s ease-in-out infinite'
                  }} />
                  <p style={{ color: colors.textTertiary, fontSize: typography.lg }}>Focus on center...</p>
                </div>
              )}

              {reactionPhase === 'stimulus' && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '120px',
                    color: colors.primary,
                    fontWeight: typography.bold,
                    lineHeight: 1,
                    animation: 'pulse 1s ease-in-out infinite'
                  }}>
                    {getStimulusIcon(currentStimulus)}
                  </div>
                </div>
              )}

              {reactionPhase === 'responded' && (
                <div style={{ textAlign: 'center' }}>
                  {lastResponseCorrect ? (
                    <>
                      <TrendingUp size={64} color={colors.successText} style={{ margin: '0 auto 1rem' }} />
                      <p style={{ fontSize: typography['2xl'], fontWeight: typography.bold, color: colors.successText }}>
                        {reactionStats.times[reactionStats.times.length - 1]}ms
                      </p>
                    </>
                  ) : (
                    <>
                      <div style={{ 
                        fontSize: '64px', 
                        color: colors.error, 
                        marginBottom: '1rem',
                        fontWeight: typography.bold
                      }}>
                        ✗
                      </div>
                      <p style={{ fontSize: typography['2xl'], fontWeight: typography.bold, color: colors.error }}>
                        Incorrect
                      </p>
                      <p style={{ fontSize: typography.sm, color: colors.textMuted, marginTop: '0.5rem' }}>
                        Try again
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Response Grid */}
            {reactionPhase === 'stimulus' && (
              <div style={{ marginBottom: mobile ? '0.5rem' : '1rem' }}>
                {reactionMode === 2 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: mobile ? '0.5rem' : '0.75rem' }}>
                    <button onClick={() => handleReactionResponse('left')} style={{ ...cardStyle, padding: mobile ? '1rem' : '1.5rem', fontSize: mobile ? '1.5rem' : '2rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>←</button>
                    <button onClick={() => handleReactionResponse('right')} style={{ ...cardStyle, padding: mobile ? '1rem' : '1.5rem', fontSize: mobile ? '1.5rem' : '2rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>→</button>
                  </div>
                )}
                {reactionMode === 4 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: mobile ? '0.5rem' : '0.75rem' }}>
                    <div />
                    <button onClick={() => handleReactionResponse('up')} style={{ ...cardStyle, padding: mobile ? '1rem' : '1.5rem', fontSize: mobile ? '1.5rem' : '2rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↑</button>
                    <div />
                    <button onClick={() => handleReactionResponse('left')} style={{ ...cardStyle, padding: mobile ? '1rem' : '1.5rem', fontSize: mobile ? '1.5rem' : '2rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>←</button>
                    <div />
                    <button onClick={() => handleReactionResponse('right')} style={{ ...cardStyle, padding: mobile ? '1rem' : '1.5rem', fontSize: mobile ? '1.5rem' : '2rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>→</button>
                    <div />
                    <button onClick={() => handleReactionResponse('down')} style={{ ...cardStyle, padding: mobile ? '1rem' : '1.5rem', fontSize: mobile ? '1.5rem' : '2rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↓</button>
                    <div />
                  </div>
                )}
                {reactionMode === 8 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: mobile ? '0.4rem' : '0.75rem' }}>
                    <button onClick={() => handleReactionResponse('up-left')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↖</button>
                    <button onClick={() => handleReactionResponse('up')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↑</button>
                    <button onClick={() => handleReactionResponse('up-right')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↗</button>
                    <button onClick={() => handleReactionResponse('left')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>←</button>
                    <div style={{ border: `1px solid ${colors.borderLight}`, borderRadius: radius.lg }} />
                    <button onClick={() => handleReactionResponse('right')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>→</button>
                    <button onClick={() => handleReactionResponse('down-left')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↙</button>
                    <button onClick={() => handleReactionResponse('down')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↓</button>
                    <button onClick={() => handleReactionResponse('down-right')} style={{ ...cardStyle, padding: mobile ? '0.75rem' : '1rem', fontSize: mobile ? '1.25rem' : '1.5rem', color: colors.primary, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.transform = 'scale(0.98)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.transform = 'scale(1)'; }}>↘</button>
                  </div>
                )}
              </div>
            )}

            <p style={{ textAlign: 'center', color: colors.textTertiary, fontSize: typography.sm }}>
              {reactionMode === 2 && 'Use arrow keys: ← →'}
              {reactionMode === 4 && 'Use arrow keys: ↑ ↓ ← →'}
              {reactionMode === 8 && 'Use arrow keys + Q E Z C for diagonals'}
            </p>
          </div>
        )}
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </>
  );
};

export default SpeedGamePage;

